import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PricingTier } from '../types/model.types';
import { Plus, Trash2 } from 'lucide-react';

interface TierEditorProps {
  tiers: PricingTier[];
  onChange: (tiers: PricingTier[]) => void;
  disabled?: boolean;
}

export function TierEditor({ tiers, onChange, disabled }: TierEditorProps) {
  const handleAdd = () => {
    const lastTier = tiers[tiers.length - 1];
    const newMin = lastTier ? (lastTier.maxQuantity ?? lastTier.minQuantity + 10) + 1 : 1;
    onChange([
      ...tiers,
      {
        minQuantity: newMin,
        maxQuantity: null,
        unitPrice: 0,
        discount: null,
        order: tiers.length,
      },
    ]);
  };

  const handleRemove = (index: number) => {
    onChange(tiers.filter((_, i) => i !== index).map((t, i) => ({ ...t, order: i })));
  };

  const handleChange = (index: number, field: keyof PricingTier, value: any) => {
    onChange(
      tiers.map((t, i) =>
        i === index ? { ...t, [field]: value === '' ? null : Number(value) } : t
      )
    );
  };

  return (
    <div className="space-y-3">
      <div className="text-sm font-medium">Niveles de Precio por Volumen</div>
      {tiers.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="text-left p-2 font-medium">Cant. Min</th>
                <th className="text-left p-2 font-medium">Cant. Max</th>
                <th className="text-left p-2 font-medium">Precio Unit.</th>
                <th className="text-left p-2 font-medium">Descuento %</th>
                <th className="p-2 w-10" />
              </tr>
            </thead>
            <tbody>
              {tiers.map((tier, index) => (
                <tr key={index} className="border-b last:border-0">
                  <td className="p-2">
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={tier.minQuantity}
                      onChange={(e) => handleChange(index, 'minQuantity', e.target.value)}
                      className="h-8 text-xs"
                      disabled={disabled}
                    />
                  </td>
                  <td className="p-2">
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={tier.maxQuantity ?? ''}
                      onChange={(e) => handleChange(index, 'maxQuantity', e.target.value)}
                      className="h-8 text-xs"
                      placeholder="∞"
                      disabled={disabled}
                    />
                  </td>
                  <td className="p-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={tier.unitPrice ?? ''}
                      onChange={(e) => handleChange(index, 'unitPrice', e.target.value)}
                      className="h-8 text-xs"
                      placeholder="0.00"
                      disabled={disabled}
                    />
                  </td>
                  <td className="p-2">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={tier.discount ?? ''}
                      onChange={(e) => handleChange(index, 'discount', e.target.value)}
                      className="h-8 text-xs"
                      placeholder="0"
                      disabled={disabled}
                    />
                  </td>
                  <td className="p-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleRemove(index)}
                      disabled={disabled}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Button type="button" variant="outline" size="sm" onClick={handleAdd} disabled={disabled}>
        <Plus className="w-4 h-4 mr-1" />
        Agregar Nivel
      </Button>
      {tiers.length === 0 && (
        <p className="text-xs text-muted-foreground">
          Define niveles de precio basados en cantidad. El precio más específico se aplica
          automaticamente.
        </p>
      )}
    </div>
  );
}

export default TierEditor;
